// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Colorspace conversion methods.
 * @author James Reid
 */

// @ts-check

// @@no-imports

// @@body
/**
 * Convert HSL colorspace pixel data to RGB colorspace pixel data.
 * @param {number} hue Hue channel value in degrees (0-359).
 * @param {number} saturation Saturation channel in percentage points (0-100).
 * @param {number} lightness Lightness channel in percentage points (0-100).
 * @param {number} alpha Alpha channel in percentage points (0-100).
 * @returns {{red:number, green:number, blue:number, alpha:number, hex:number}}
 * Colorspace converted pixel data with channel data as 8-bit integers.
 */
const hslToRgb = (hue, saturation, lightness, alpha = NaN) => {
    hue /= 60
    saturation /= 100
    lightness /= 100
    alpha *= 2.55

    const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation
    const intermediate = chroma * (1 - Math.abs(hue % 2 - 1))
    const lightnessMatchFactor = lightness - chroma / 2

    const [red, green, blue] = (hue < 1 ? [chroma, intermediate, 0]
        : hue < 2 ? [intermediate, chroma, 0]
        : hue < 3 ? [0, chroma, intermediate]
        : hue < 4 ? [0, intermediate, chroma]
        : hue < 5 ? [intermediate, 0, chroma]
        : [chroma, 0, intermediate])
        .map(channel => Math.round((channel + lightnessMatchFactor) * 255))
    const hex = isNaN(alpha)
        ? red << 16 | green << 8 | blue
        : (red << 24 | green << 16 | blue << 8 | alpha) >>> 0

    return { red, green, blue, alpha, hex }
}

/**
 * Convert RGB colorspace pixel data to HSL colorspace pixel data.
 * @param {number} red Red channel value as 8-bit integer (0-255).
 * @param {number} green Green channel value as 8-bit integer (0-255).
 * @param {number} blue Blue channel value as 8-bit integer (0-255).
 * @param {number} alpha Alpha channel value as 8-bit integer (0-255).
 * @returns {{hue:number, saturation:number, lightness:number, alpha:number}}
 * Colorspace converted pixel data with channel data as degrees of hue (0-359),
 * and percentage saturation and lightness (0-100).
 */
const rgbToHsl = (red, green, blue, alpha = NaN) => {
    red /= 255
    green /= 255
    blue /= 255

    const minChannel = Math.min(red, green, blue)
    const maxChannel = Math.max(red, green, blue)

    const chroma = maxChannel - minChannel
    const hue = 60 * (chroma === 0 ? 0
        : maxChannel === red ? (green - blue) / chroma % 6
        : maxChannel === green ? (blue - red) / chroma + 2
        : (red - green) / chroma + 4)

    const lightness = maxChannel - chroma / 2
    const saturation = lightness === 0 || lightness === 1 ? 0
        : chroma / (1 - Math.abs(2 * maxChannel - chroma - 1))

    return {
        hue: (360 + Math.round(hue)) % 360,
        saturation: Math.round(saturation * 100),
        lightness: Math.round(lightness * 100),
        alpha: Math.round(alpha / 2.55)
    }
}

// @@exports
export { hslToRgb, rgbToHsl }
